import { doc, getDocs, setDoc, increment, collection, addDoc, serverTimestamp, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

const VISITOR_KEY = 'pmp_visitor_tracked_today';

async function logVisitorEvent(type: 'visitor' | 'pageView', uid?: string, email?: string) {
  try {
    const today = new Date().toISOString().slice(0, 10);
    await addDoc(collection(db, 'visitor_logs'), {
      timestamp: serverTimestamp(),
      date: today,
      type,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      uid: uid || null,
      email: email || null,
    });
  } catch (error) {
    console.error(`Visitor log (${type}) error:`, error);
  }
}

/**
 * Track a unique visitor per day in Firestore.
 * Uses localStorage to avoid double-counting within the same day.
 */
export async function trackVisitor(): Promise<void> {
  try {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const lastTracked = localStorage.getItem(VISITOR_KEY);

    // Only track once per day per browser
    if (lastTracked === today) return;

    // Use setDoc with merge + increment — avoids needing read access
    // (Firestore rules only allow read for authenticated users, but write for all)
    const counterRef = doc(db, 'stats', 'visitors');
    await setDoc(counterRef, {
      total: increment(1),
      [`daily.${today}`]: increment(1),
    }, { merge: true });

    // Only mark as tracked after Firestore succeeds
    localStorage.setItem(VISITOR_KEY, today);

    await logVisitorEvent('visitor');
  } catch (error) {
    // Silently fail — visitor tracking should never break the app
    console.error('Visitor tracking error:', error);
  }
}

/**
 * Track every page visit in Firestore, regardless of login status or daily uniqueness.
 * This is separate from the unique-daily visitor counter.
 */
export async function trackPageView(): Promise<void> {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const counterRef = doc(db, 'stats', 'pageViews');
    await setDoc(counterRef, {
      total: increment(1),
      [`daily.${today}`]: increment(1),
    }, { merge: true });

    await logVisitorEvent('pageView');
  } catch (error) {
    console.error('Page view tracking error:', error);
  }
}

/**
 * Force track a visit (for admin testing) — bypasses localStorage once-per-day check.
 */
export async function trackVisitorForce(): Promise<void> {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const counterRef = doc(db, 'stats', 'visitors');
    await setDoc(counterRef, {
      total: increment(1),
      [`daily.${today}`]: increment(1),
    }, { merge: true });
  } catch (error) {
    console.error('Force visitor tracking error:', error);
    throw error;
  }
}

/**
 * Recalculate visitor stats from visitor_logs and update the stats/visitors document.
 * Useful when the aggregated counter is out of sync with the log collection.
 */
export async function recalcVisitorStatsFromLogs(): Promise<{ total: number; today: number }> {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const allLogs = await getVisitorLogs(10000);
    const visitorLogs = allLogs.filter((log) => log.type === 'visitor' || !log.type);
    const total = visitorLogs.length;
    const todayCount = visitorLogs.filter((log) => log.date === today).length;

    const counterRef = doc(db, 'stats', 'visitors');
    await setDoc(counterRef, {
      total,
      [`daily.${today}`]: todayCount,
    }, { merge: true });

    return { total, today: todayCount };
  } catch (error) {
    console.error('Failed to recalc visitor stats:', error);
    throw error;
  }
}

/**
 * Fetch recent visitor logs with date and time.
 */
export async function getVisitorLogs(limitCount: number = 50): Promise<any[]> {
  try {
    const q = query(collection(db, 'visitor_logs'), orderBy('timestamp', 'desc'), limit(limitCount));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Failed to get visitor logs:', error);
  }
  return [];
}

/**
 * Seed or reset the visitor counter (admin only).
 */
export async function seedVisitorCounter(total: number, todayCount: number): Promise<void> {
  const today = new Date().toISOString().slice(0, 10);
  const counterRef = doc(db, 'stats', 'visitors');
  await setDoc(counterRef, {
    total,
    daily: { [today]: todayCount },
  }, { merge: true });
}

/**
 * Get total and today's page view counts by counting visitor_logs directly.
 */
export async function getPageViewStats(): Promise<{ total: number; today: number }> {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const q = query(collection(db, 'visitor_logs'), where('type', '==', 'pageView'));
    const snap = await getDocs(q);
    let total = 0;
    let todayCount = 0;
    snap.docs.forEach((d) => {
      const data = d.data();
      total++;
      if (data.date === today) todayCount++;
    });
    return { total, today: todayCount };
  } catch (error) {
    console.error('Failed to get page view stats from logs:', error);
  }
  return { total: 0, today: 0 };
}

/**
 * Get total and today's visitor counts by counting visitor_logs directly.
 */
export async function getVisitorStats(): Promise<{ total: number; today: number }> {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const q = query(collection(db, 'visitor_logs'), where('type', 'in', ['visitor', 'pageView']));
    const snap = await getDocs(q);
    let total = 0;
    let todayCount = 0;
    snap.docs.forEach((d) => {
      const data = d.data();
      total++;
      if (data.date === today) todayCount++;
    });
    return { total, today: todayCount };
  } catch (error) {
    console.error('Failed to get visitor stats from logs:', error);
  }
  return { total: 0, today: 0 };
}
